import { Box, Card, Flex, IconWithVariant } from '@auviknetworks/iceberg-ui';
import {
    Button,
    Checkbox,
    Collapse,
    FormControl,
    FormControlLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';
import { Notify } from 'application/Shared/Helpers';
import { formatDistanceToNow } from 'application/Shared/Helpers/Dates';
import { Confirm } from 'application/Shared/Modals/Confirm';
import { Region } from 'application/Shared/types/tenant';
import { useUpdateCustomerManifests } from 'hooks/dataAccess/Workato';
import { useFetchManifests } from 'hooks/dataAccess/Workato/useFetchManifests';
import { useUploadManifest } from 'hooks/dataAccess/Workato/useUploadManifest';
import React, { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export const ManageWorkatoManifests = ({
    platformTenantIds = [],
    disableS3Upload = false,
    region
}: {
    platformTenantIds?: string[];
    disableS3Upload?: boolean;
    region?: Region;
}) => {
    const { t } = useTranslation();
    const [openFolder, setOpenFolder] = useState(true);

    const { data, refetchData } = useFetchManifests();
    const { uploadManifest } = useUploadManifest();
    const folderName = 'saasOps/';
    const fileInputRef = useRef<HTMLInputElement>(null);

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
    };

    const handleFileUpload = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const file = event.target.files?.[0];
        if (file) {
            await uploadManifest(file);
            refetchData();
        }
    };

    return (
        <Card>
            <Flex
                sx={{
                    justifyContent: 'space-between',
                    padding: '8px 12px'
                }}
            >
                <Typography variant="h4" sx={{ padding: '4px 12px' }}>
                    {t('Admin.Manifests.current')}
                </Typography>
            </Flex>
            <Paper sx={{ margin: 2, maxHeight: 400, overflow: 'auto' }}>
                <List>
                    <ListItem
                        onClick={() => setOpenFolder(!openFolder)}
                        sx={{
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                        }}
                    >
                        <ListItemIcon>
                            <IconWithVariant
                                variant="primary"
                                src="OpenFolder"
                            />
                        </ListItemIcon>
                        <ListItemText primary={folderName} />
                    </ListItem>
                    <Collapse in={openFolder} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {data?.Contents?.sort((a, b) => {
                                if (a.LastModified && b.LastModified) {
                                    return (
                                        new Date(b.LastModified).getTime() -
                                        new Date(a.LastModified).getTime()
                                    );
                                }
                                return 0;
                            })
                                .filter((file) => file.Key !== folderName)
                                .map((file) => (
                                    <ListItem
                                        key={file.Key}
                                        sx={{
                                            '&:hover': {
                                                '& .deploy-button': {
                                                    display: 'block'
                                                },
                                                backgroundColor:
                                                    'rgba(0, 0, 0, 0.04)'
                                            },
                                            borderBottom:
                                                '1px solid rgba(0, 0, 0, 0.12)',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            pl: 4
                                        }}
                                    >
                                        <Flex sx={{ alignItems: 'center' }}>
                                            <ListItemIcon>
                                                <IconWithVariant
                                                    src="ZipFolder"
                                                    variant="primary"
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={file.Key?.replace(
                                                    folderName,
                                                    ''
                                                )}
                                                secondary={
                                                    <>
                                                        <Typography component="span">
                                                            {formatFileSize(
                                                                file.Size || 0
                                                            )}
                                                        </Typography>
                                                        {' • '}
                                                        {file.LastModified &&
                                                            formatDistanceToNow(
                                                                file.LastModified
                                                            )}
                                                    </>
                                                }
                                            />
                                        </Flex>
                                        <UpdateWorkatoManifest
                                            platformTenantIds={
                                                platformTenantIds
                                            }
                                            manifestFilePath={file.Key!}
                                            providedTenantRegion={region}
                                        />
                                    </ListItem>
                                ))}
                        </List>
                    </Collapse>
                </List>
            </Paper>
            {!disableS3Upload && (
                <Flex
                    sx={{
                        padding: '8px 12px'
                    }}
                >
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileUpload}
                        accept=".zip"
                    />
                    <Button
                        data-trackid="globalAdmin_saasOps_uploadManifest"
                        variant="contained"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {t('Admin.Manifests.upload')}
                    </Button>
                </Flex>
            )}
        </Card>
    );
};

const UpdateWorkatoManifest = ({
    manifestFilePath,
    platformTenantIds = [],
    providedTenantRegion
}: {
    manifestFilePath: string;
    platformTenantIds?: string[];
    providedTenantRegion?: Region;
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    // Default to US region if one is not provided.
    const [region, setRegion] = useState<Region>(
        providedTenantRegion || Region.US
    );
    const [newRecipesAdded, setNewRecipesAdded] = useState(false);

    const { updateManifest } = useUpdateCustomerManifests();

    const onConfirm = async () => {
        const { error } = await updateManifest({
            importOnly: !newRecipesAdded,
            manifestFilePath,
            platformTenantIdArray: platformTenantIds,
            region,
            setLoading
        });
        if (error) {
            Notify.e(t('Admin.Manifests.update.error'));
        } else {
            Notify.s(t('Admin.Manifests.update.success'));
        }

        setOpen(false);
    };

    return (
        <>
            <Button
                data-trackid="globalAdmin_saasOps_deployManifest"
                onClick={() => setOpen(true)}
                className="deploy-button"
                sx={{
                    display: 'none',
                    marginRight: 2
                }}
                color="primary"
                variant="contained"
            >
                {t('Admin.Manifests.deploy')}
            </Button>
            {open && (
                <Confirm
                    modalOpen={open}
                    promptConfirmFn={onConfirm}
                    promptCancelFn={() => setOpen(false)}
                    promptTitle={t('Common.confirm')}
                    promptText={
                        <>
                            <RegionSelector
                                manifestFilePath={manifestFilePath}
                                region={region}
                                onRegionChange={setRegion}
                                disableOptions={!!providedTenantRegion}
                                platformTenantIds={platformTenantIds}
                            />
                            <Flex>
                                <NewRecipesStatus
                                    newRecipesAdded={newRecipesAdded}
                                    onRecipesAddedChange={setNewRecipesAdded}
                                />
                            </Flex>
                        </>
                    }
                    loading={loading}
                />
            )}
        </>
    );
};

interface RegionSelectorProps {
    disableOptions: boolean;
    manifestFilePath: string;
    onRegionChange: (region: Region) => void;
    platformTenantIds?: string[];
    region: string;
}

interface NewRecipesStatusProps {
    newRecipesAdded: boolean;
    onRecipesAddedChange: (value: boolean) => void;
}

const RegionSelector = ({
    manifestFilePath,
    region,
    onRegionChange,
    disableOptions,
    platformTenantIds
}: RegionSelectorProps) => (
    <>
        <Typography>
            {platformTenantIds?.length ? (
                <>
                    <Trans
                        i18nKey="Admin.Manifests.update.selectTenants.message"
                        values={{
                            manifestFilePath,
                            region: region.toUpperCase()
                        }}
                    />
                    <Box sx={{ marginY: '16px' }}>
                        {platformTenantIds.map((id) => (
                            <Box marginBottom="4px" key={id}>
                                {id}
                            </Box>
                        ))}
                    </Box>
                </>
            ) : (
                <Trans
                    i18nKey="Admin.Manifests.update.allTenants.message"
                    values={{ manifestFilePath, region: region.toUpperCase() }}
                />
            )}
        </Typography>
        <FormControl>
            <RadioGroup
                row // Add this prop
                value={region}
                onChange={(e) => onRegionChange(e.target.value as Region)}
            >
                <FormControlLabel
                    value={Region.US}
                    control={<Radio disabled={disableOptions} />}
                    label="US"
                />
                <FormControlLabel
                    value={Region.EU}
                    control={<Radio disabled={disableOptions} />}
                    label="EU"
                />
            </RadioGroup>
        </FormControl>
    </>
);

const NewRecipesStatus = ({
    newRecipesAdded,
    onRecipesAddedChange
}: NewRecipesStatusProps) => {
    const { t } = useTranslation();

    return (
        <FormControl>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={newRecipesAdded}
                        onChange={(e) => {
                            onRecipesAddedChange(e.target.checked);
                        }}
                    />
                }
                label={t('Admin.Manifests.newRecipes.title')}
            />
            <Typography sx={{ paddingLeft: '30px' }}>
                {t('Admin.Manifests.newRecipes.description')}
            </Typography>
        </FormControl>
    );
};
